import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useApi from '../hooks/useApi';
import { PROGRAMS } from '../constants/programs';
import AppSheetMrtTable from './AppSheetMrtTable';
import AppSheetStatusChangesSummary from './AppSheetStatusChangesSummary';

const ALL_PROGRAMS_OPTION = 'All Programs';
const PROGRAM_OPTIONS = [ALL_PROGRAMS_OPTION, ...PROGRAMS];

const DEFAULT_PAGE_SIZE = 50;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const resolveQuantity = (data) => {
  if (!data) return null;
  const keys = [
    'itemQuantity',
    'ItemQuantity',
    'item_quantity',
    'Item_Quantity',
    'quantity',
    'Quantity',
    'itemQty',
    'ItemQty',
  ];
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    const raw = data[key];
    if (raw === null || raw === undefined || raw === '') continue;
    const parsed = Number(String(raw).trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const AppSheetStatusChangesReport = () => {
  const { useAppSheetStatusChanges } = useApi();
  const [trackingConfig, setTrackingConfig] = useState(null);
  const [table, setTable] = useState(null);
  const [loadingTables, setLoadingTables] = useState(true);
  const [tableError, setTableError] = useState(null);

  const [programName, setProgramName] = useState(ALL_PROGRAMS_OPTION);
  const [rowKey, setRowKey] = useState('');
  const [activePreset, setActivePreset] = useState('last7');
  const [startDate, setStartDate] = useState(dayjs().subtract(6, 'day').startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });

  useEffect(() => {
    let active = true;
    const loadTableConfig = async () => {
      setLoadingTables(true);
      setTableError(null);
      try {
        const [trackingRes, tablesRes] = await Promise.all([
          axios.get('/api/appsheet/status-tracking'),
          axios.get('/api/appsheet/tables'),
        ]);
        if (!active) return;
        const tracking = trackingRes.data || {};
        setTrackingConfig(tracking);
        const tables = Array.isArray(tablesRes.data?.tables) ? tablesRes.data.tables : [];
        const matched = tracking.tableName
          ? tables.find((t) => t.name === tracking.tableName)
          : null;
        setTable(matched || null);
      } catch (error) {
        if (!active) return;
        setTableError(error?.response?.data?.error || error?.message || 'Failed to load AppSheet table metadata.');
      } finally {
        if (active) setLoadingTables(false);
      }
    };
    void loadTableConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (activePreset === 'custom') return;
    const now = dayjs();
    if (activePreset === 'today') {
      setStartDate(now.startOf('day'));
      setEndDate(now.endOf('day'));
    } else if (activePreset === 'last7') {
      setStartDate(now.subtract(6, 'day').startOf('day'));
      setEndDate(now.endOf('day'));
    } else if (activePreset === 'last30') {
      setStartDate(now.subtract(29, 'day').startOf('day'));
      setEndDate(now.endOf('day'));
    }
  }, [activePreset]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [programName, rowKey, startDate, endDate]);

  const queryOptions = useMemo(() => {
    return {
      programName: programName === ALL_PROGRAMS_OPTION ? null : programName,
      rowKey: rowKey ? rowKey.trim() : null,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
    };
  }, [programName, startDate, endDate, pagination, rowKey]);

  const { data, isLoading, error } = useAppSheetStatusChanges(queryOptions);

  const columns = useMemo(() => {
    const fieldColumns = (table?.fields || []).map((field) => ({
      id: field.name,
      header: field.displayName || field.name,
      size: 180,
      accessorFn: (row) => row?.[field.name],
    }));
    return [
      {
        id: '__changedAt',
        header: 'Changed At',
        size: 180,
        accessorFn: (row) => formatDateTime(row?.__changedAt),
      },
      {
        id: '__previousStatus',
        header: 'Previous Status',
        size: 180,
        accessorFn: (row) => row?.__previousStatus || '—',
      },
      {
        id: '__currentStatus',
        header: 'Current Status',
        size: 180,
        accessorFn: (row) => row?.__currentStatus || '—',
      },
      ...fieldColumns,
    ];
  }, [table?.fields]);

  const rows = useMemo(() => {
    const items = Array.isArray(data?.rows) ? data.rows : [];
    return items
      .filter((entry) => {
        const qtyValue = resolveQuantity(entry?.rowData);
        return qtyValue !== null && qtyValue > 0;
      })
      .map((entry) => {
        const oldStatus = entry?.statusOld ? String(entry.statusOld) : '';
        const newStatus = entry?.statusNew ? String(entry.statusNew) : '';
        return {
          __rowId: entry?.id,
          __changedAt: entry?.changedAt,
          __previousStatus: oldStatus || '—',
          __currentStatus: newStatus || '—',
          ...(entry?.rowData || {}),
        };
      });
  }, [data?.rows]);

  const showLoading = loadingTables || isLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
        <AppSheetStatusChangesSummary programName={programName === ALL_PROGRAMS_OPTION ? null : programName} />
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Program</InputLabel>
              <Select
                value={programName}
                label="Program"
                onChange={(e) => setProgramName(e.target.value)}
              >
                {PROGRAM_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Today"
                clickable
                onClick={() => setActivePreset('today')}
                color={activePreset === 'today' ? 'primary' : 'default'}
                variant={activePreset === 'today' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Last 7 days"
                clickable
                onClick={() => setActivePreset('last7')}
                color={activePreset === 'last7' ? 'primary' : 'default'}
                variant={activePreset === 'last7' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Last 30 days"
                clickable
                onClick={() => setActivePreset('last30')}
                color={activePreset === 'last30' ? 'primary' : 'default'}
                variant={activePreset === 'last30' ? 'filled' : 'outlined'}
              />
            </Box>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => {
                setStartDate(value ? value.startOf('day') : null);
                setActivePreset('custom');
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => {
                setEndDate(value ? value.endOf('day') : null);
                setActivePreset('custom');
              }}
              slotProps={{ textField: { size: 'small' } }}
            />
            <TextField
              size="small"
              label="Row Key (Id)"
              value={rowKey}
              onChange={(e) => setRowKey(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Box>
        </LocalizationProvider>

        {tableError && <Alert severity="error">{tableError}</Alert>}
        {error && <Alert severity="error">{error?.message || 'Failed to load AppSheet status changes.'}</Alert>}
        {showLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!showLoading && !error && !table && (
          <Alert severity="warning">Configured AppSheet table not found. Verify Settings → AppSheet.</Alert>
        )}
        {!showLoading && !error && table && (
          rows.length > 0 ? (
            <AppSheetMrtTable
              rows={rows}
              columns={columns}
              rowCount={data?.totalRows ?? rows.length}
              isLoading={isLoading}
              pagination={pagination}
              onPaginationChange={(updater) => {
                const next = typeof updater === 'function' ? updater(pagination) : updater;
                setPagination(next);
              }}
              title={`${trackingConfig?.tableName || 'AppSheet'} status changes`}
              manualPagination={true}
              manualFiltering={false}
              manualSorting={false}
            />
          ) : (
            <Alert severity="info">No AppSheet status changes found for this range.</Alert>
          )
        )}
      </Paper>
    </Box>
  );
};

export default AppSheetStatusChangesReport;
